import React, { useMemo } from 'react';
import {
    Box,
    Chip,
    Container,
    Grid,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useStatisticsQuery } from '../hooks/statistics';
import { ErrorTryRefetch } from '../core/ErrorTryRefetch';
import PageHeader from '../core/PageHeader';
import PvtaCard from '../core/PvtaCard';

type SeriesPoint = {
    label: string;
    logins: number;
    mails: number;
    reservations: number;
};

type MetricKey = 'logins' | 'mails' | 'reservations';

type MetricDef = {
    metricKey: MetricKey;
    title: string;
    color: string;
    last30: number;
    total: number;
    subtitle: string;
    totalLabel: string;
};

// Diagramm-Farben als Hex-Literale: werden u. a. als SVG-stroke gesetzt, wo
// CSS-var()-Tokens nicht zuverlässig auflösen. Werte im PVTA-Branding gehalten
// (Grün = --pvta-green-700, Orange = --pvta-favorite).
const KPI_COLORS: Record<MetricKey, string> = {
    logins: '#1f4d2b',
    mails: '#1f6f8b',
    reservations: '#c46f2f',
};

const formatNumber = (value: number): string => new Intl.NumberFormat('de-DE').format(value);

const formatDateLabel = (rawLabel: string): string => {
    const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(rawLabel);
    if (!isoDateMatch) {
        return rawLabel;
    }
    const [, , month, day] = isoDateMatch;
    return `${day}.${month}.`;
};

const StatisticsKpiCard: React.FC<MetricDef> = ({ title, color, last30, total, subtitle, totalLabel }) => {
    const ratio = total > 0 ? Math.min((last30 / total) * 100, 100) : 0;

    return (
        <PvtaCard sx={{ p: { xs: 2, sm: 2.5 }, height: '100%', minWidth: 0 }}>
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 0 }}>
                        {title}
                    </Typography>
                    <Chip
                        label={`${ratio.toFixed(1)}%`}
                        size="small"
                        sx={{
                            backgroundColor: `${color}20`,
                            color,
                            fontWeight: 700,
                            flexShrink: 0,
                        }}
                    />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="baseline">
                    <Typography variant="h4" sx={{ fontWeight: 700, color }}>
                        {formatNumber(last30)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Letzte 30 Tage
                    </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {totalLabel}: <strong>{formatNumber(total)}</strong>
                </Typography>
            </Stack>
        </PvtaCard>
    );
};

const StatisticsLoadingState: React.FC = () => (
    <Grid container spacing={3}>
        {[0, 1, 2].map((item) => (
            <Grid item xs={12} md={4} key={item}>
                <PvtaCard sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
                    <Skeleton variant="text" width="50%" height={30} />
                    <Skeleton variant="text" width="30%" height={52} />
                    <Skeleton variant="text" width="70%" height={24} />
                </PvtaCard>
            </Grid>
        ))}
        <Grid item xs={12}>
            <PvtaCard sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
                <Skeleton variant="text" width="40%" height={30} sx={{ mb: 1.5 }} />
                <Skeleton variant="rectangular" height={420} />
            </PvtaCard>
        </Grid>
    </Grid>
);

const StatisticsEmptyState: React.FC = () => (
    <PvtaCard
        sx={{
            py: 6,
            px: { xs: 2, sm: 2.5 },
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
        }}
    >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Keine Statistikdaten vorhanden
        </Typography>
        <Typography variant="body2" color="text.secondary">
            Sobald Daten vorliegen, werden Kennzahlen und Verlaeufe hier angezeigt.
        </Typography>
    </PvtaCard>
);

const LineSeriesChart: React.FC<{ points: SeriesPoint[]; isMobile: boolean; isTablet: boolean }> = ({
    points,
    isMobile,
    isTablet,
}) => {
    const width = 1200;
    const height = isMobile ? 300 : isTablet ? 360 : 460;
    const padding = isMobile ? 44 : 56;

    const maxValue = useMemo(() => {
        const allValues = points.flatMap((point) => [point.logins, point.mails, point.reservations]);
        return Math.max(1, ...allValues);
    }, [points]);

    const x = (index: number): number => {
        if (points.length <= 1) {
            return width / 2;
        }
        return padding + (index * (width - padding * 2)) / (points.length - 1);
    };

    const y = (value: number): number => {
        const chartHeight = height - padding * 2;
        return height - padding - (value / maxValue) * chartHeight;
    };

    const polylineFor = (key: MetricKey): string =>
        points.map((point, index) => `${x(index)},${y(point[key])}`).join(' ');

    const labelStep = Math.max(1, Math.ceil(points.length / (isMobile ? 5 : 10)));
    const shouldRenderLabel = (index: number): boolean =>
        index === 0 || index === points.length - 1 || index % labelStep === 0;

    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                {[
                    { label: 'Anmeldungen', color: KPI_COLORS.logins },
                    { label: 'Mails', color: KPI_COLORS.mails },
                    { label: 'Reservierungsanfragen', color: KPI_COLORS.reservations },
                ].map((entry) => (
                    <Stack direction="row" spacing={1} alignItems="center" key={entry.label}>
                        <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: entry.color }} />
                        <Typography variant="body2" color="text.secondary">
                            {entry.label}
                        </Typography>
                    </Stack>
                ))}
            </Stack>

            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Box sx={{ minWidth: isMobile ? 0 : 760 }}>
                    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label="30-Tage-Verlauf">
                        {[0, 1, 2, 3, 4].map((tick) => {
                            const value = (maxValue / 4) * (4 - tick);
                            const yPos = padding + ((height - padding * 2) / 4) * tick;
                            return (
                                <g key={tick}>
                                    <line
                                        x1={padding}
                                        y1={yPos}
                                        x2={width - padding}
                                        y2={yPos}
                                        stroke="#e6e1d9"
                                        strokeWidth="1"
                                    />
                                    <text x={12} y={yPos + 4} fill="#6b727c" fontSize={isMobile ? 11 : 12}>
                                        {Math.round(value)}
                                    </text>
                                </g>
                            );
                        })}
                        <polyline
                            fill="none"
                            stroke={KPI_COLORS.logins}
                            strokeWidth="3"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            points={polylineFor('logins')}
                        />
                        <polyline
                            fill="none"
                            stroke={KPI_COLORS.mails}
                            strokeWidth="3"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            points={polylineFor('mails')}
                        />
                        <polyline
                            fill="none"
                            stroke={KPI_COLORS.reservations}
                            strokeWidth="3"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            points={polylineFor('reservations')}
                        />
                        {points.map((point, index) => (
                            shouldRenderLabel(index) ? (
                                <text
                                    key={`${point.label}-${index}`}
                                    x={x(index)}
                                    y={height - 16}
                                    textAnchor="middle"
                                    fill="#6b727c"
                                    fontSize={isMobile ? '11' : '12'}
                                >
                                    {formatDateLabel(point.label)}
                                </text>
                            ) : null
                        ))}
                    </svg>
                </Box>
            </Box>
        </Box>
    );
};

const StatisticsPage: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const {
        data,
        isLoading,
        isError,
        refetch,
    } = useStatisticsQuery();

    if (isLoading) {
        return (
            <Box sx={{ py: { xs: 3, md: 4 } }}>
                <Container maxWidth="xl">
                    <StatisticsLoadingState />
                </Container>
            </Box>
        );
    }

    if (isError || !data) {
        return <ErrorTryRefetch refetchFunction={refetch} />;
    }

    const points: SeriesPoint[] = data.series_last_30_days.labels.map((label, index) => ({
        label,
        logins: data.series_last_30_days.logins[index] ?? 0,
        mails: data.series_last_30_days.mails[index] ?? 0,
        reservations: data.series_last_30_days.reservations[index] ?? 0,
    }));

    const metrics: MetricDef[] = [
        {
            metricKey: 'logins',
            title: 'Anmeldungen',
            color: KPI_COLORS.logins,
            last30: data.last_30_days.logins_30d,
            total: data.totals.logins_total,
            subtitle: 'Nutzer mit Login',
            totalLabel: 'Gesamt',
        },
        {
            metricKey: 'mails',
            title: 'Mails',
            color: KPI_COLORS.mails,
            last30: data.last_30_days.mails_30d,
            total: data.totals.mails_total,
            subtitle: 'Versendete Mails',
            totalLabel: 'Gesamt',
        },
        {
            metricKey: 'reservations',
            title: 'Reservierungsanfragen',
            color: KPI_COLORS.reservations,
            last30: data.last_30_days.reservations_30d,
            total: data.totals.reservations_total,
            subtitle: 'Reservierungsanfragen',
            totalLabel: 'Gesamt',
        },
    ];

    const hasAnyData = metrics.some((metric) => metric.last30 > 0 || metric.total > 0) || points.length > 0;

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
                <PageHeader title="Statistiken" subtitle="Auswertungen und Kennzahlen des Vereins." />
                <Stack spacing={{ xs: 2.5, md: 3 }}>
                    {!hasAnyData ? (
                        <StatisticsEmptyState />
                    ) : (
                        <Box sx={{ width: '100%', maxWidth: '100%', mx: 'auto' }}>
                            <Grid container spacing={{ xs: 2, md: 3 }}>
                                {metrics.map((metric) => (
                                    <Grid item xs={12} md={4} key={metric.metricKey}>
                                        <StatisticsKpiCard {...metric} />
                                    </Grid>
                                ))}

                                <Grid item xs={12}>
                                    <PvtaCard sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                30-Tage-Verlauf
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Anmeldungen, Mails und Reservierungsanfragen
                                            </Typography>
                                        </Box>
                                        {points.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                Keine Verlaufsdaten verfuegbar.
                                            </Typography>
                                        ) : (
                                            <LineSeriesChart points={points} isMobile={isMobile} isTablet={isTablet} />
                                        )}
                                    </PvtaCard>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Stack>
            </Container>
        </Box>
    );
};

export default StatisticsPage;
